import React from 'react';
import { observer } from 'mobx-react-lite';
import styled, { ThemeProps } from 'styled-components';
import { H3, H1, P1, P2, P3, P4 } from 'styleguide/Texts';
import { colors } from 'styleguide/colors';
import { ThemedHevyProLogo } from 'components/svg/ThemedHevyProLogo';
import { PrimaryButton } from 'styleguide/Buttons';
import { DividerLine } from 'styleguide/DividerLine';
import { MicroIcon } from 'styleguide/Icons/MicroIcon';
import MastercardLogo from '../Paywall/components/assets/Mastercard.svg';
import StripeLogo from '../Paywall/components/assets/Stripe.svg';
import VisaLogo from '../Paywall/components/assets/Visa.svg';
import { UnauthenticatedPaywallViewModel } from './UnauthenticatedPaywallViewModel';
import { Theme } from 'styleguide/types';
import { FAQCard } from 'screens/Paywall/components/FAQCard';
import { PaywallCard } from 'screens/Paywall/components/PaywallCard';
import { ReviewCard } from 'screens/Paywall/components/ReviewCard';
import { SwipeableCards } from 'screens/Paywall/components/SwipeableCards';
import { Ratings } from 'screens/Paywall/components/Ratings';
import FeaturedLogoCarousel from 'screens/Paywall/components/FeaturedLogoCarousel';
import { t } from 'translations/t';
import { formatDateTime } from 'translations/formatters';
import dayjs from 'dayjs';

const MOBILE_BREAKPOINT = 1270;

const Content = styled.div`
  margin: 20px 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const CardContainer = styled.div`
  display: grid;
  grid-template-columns: 470px 470px;
  column-gap: 40px;
  margin-bottom: 24;
  grid-auto-flow: dense;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 360px;
  }
`;

const CardLeft = styled.div`
  padding: 40px;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  background-color: ${(props: ThemeProps<Theme>) =>
    props.theme.name === 'light' ? colors.greyUltraLight : colors.greyDark};
  border-radius: 8px;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-column: 1;
    grid-row: 4;
    padding: 20px;
    align-items: center;
  }
`;

const CardRight = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-column: 1;
  }
`;

const PlanComparisonTable = styled.table`
  border-collapse: collapse;
  width: 100%;
`;

const PlanComparisonItem = styled.tr`
  height: 44px;
  border-top: 1px solid ${colors.greyLight};
`;

const ItemName = ({ children }: { children: React.ReactNode }) => {
  return (
    <td>
      <P3>{children}</P3>
    </td>
  );
};

const ItemPlanContainer = styled.td`
  padding: 0 16px;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0 4px;
  }
`;

const ItemPlan = ({ children }: { children: React.ReactNode }) => {
  return (
    <ItemPlanContainer>
      <P3 type="secondary">{children}</P3>
    </ItemPlanContainer>
  );
};

const BottomContent = styled.div`
  width: 100%;
  max-width: 980px;
  flex-direction: row;
  text-align: center;
`;

const PaymentMethods = styled.div`
  margin-bottom: 20px;
  display: grid;
  grid-template-columns: auto auto auto auto auto;
  gap: 8px;
  align-items: center;
  justify-content: center;
`;

const UserReviews = styled.div`
  margin-bottom: 96px;
  display: grid;
  grid-template-columns: 400px 400px 400px;
  gap: 40px;
  justify-content: center;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const ReviewCardMobileContainer = styled.div`
  cursor: pointer;
  user-select: none;
  height: 400px;
  margin: 0 8px;
`;

const MobileContent = styled.div`
  display: none;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: inherit;
  }
`;

const FeaturedContainer = styled.div`
  margin-bottom: 20px;
`;

const FAQList = styled.div`
  width: 100%;
  height: 560px;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const getUserReviews = () => {
  const dateFormatProps: Intl.DateTimeFormatOptions = {
    dateStyle: 'long',
  };

  const reviews: {
    title: string;
    date: string;
    author: string;
    description: string;
  }[] = [
    {
      title: t('web.paywall.review0.title'),
      date: formatDateTime(dayjs('2026-01-03').toDate(), dateFormatProps),
      author: 'Lauren A.S.',
      description: t('web.paywall.review0.desc'),
    },
    {
      title: t('web.paywall.review1.title'),
      date: formatDateTime(dayjs('2026-01-02').toDate(), dateFormatProps),
      author: '3m11e',
      description: t('web.paywall.review1.desc'),
    },
    {
      title: t('web.paywall.review2.title'),
      date: formatDateTime(dayjs('2026-01-02').toDate(), dateFormatProps),
      author: 'Franiie R.',
      description: t('web.paywall.review2.desc'),
    },
    {
      title: t('web.paywall.review3.title'),
      date: formatDateTime(dayjs('2026-01-02').toDate(), dateFormatProps),
      author: 'DP2gnarly',
      description: t('web.paywall.review3.desc'),
    },
    {
      title: t('web.paywall.review4.title'),
      date: formatDateTime(dayjs('2026-01-02').toDate(), dateFormatProps),
      author: 'Johnavenlee95',
      description: t('web.paywall.review4.desc'),
    },
    {
      title: t('web.paywall.review5.title'),
      date: formatDateTime(dayjs('2026-01-02').toDate(), dateFormatProps),
      author: 'DillonJQuinn',
      description: t('web.paywall.review5.desc'),
    },
    {
      title: t('web.paywall.review6.title'),
      date: formatDateTime(dayjs('2026-01-02').toDate(), dateFormatProps),
      author: 'ghost of april',
      description: t('web.paywall.review6.desc'),
    },
  ];

  return reviews.sort(() => Math.random() - 0.5);
};

const renderFAQs = (vm: UnauthenticatedPaywallViewModel) => {
  const faqs: {
    title: string;
    text: string;
  }[] = [
    {
      title: t('paywall.faq.include.question'),
      text: t('paywall.faq.include.answer'),
    },
    {
      title: t('paywall.faq.cancel.question'),
      text: t('paywall.faq.cancel.answer'),
    },
    {
      title: t('web.paywall.faq2.q'),
      text: t('web.paywall.faq2.a'),
    },
    {
      title: t('paywall.faq.switchingPlatforms.question'),
      text: t('paywall.faq.switchingPlatforms.answer'),
    },
    {
      title: t('paywall.faq.lifetime.question'),
      text: t('paywall.faq.lifetime.answer'),
    },
    {
      title: t('paywall.faq.switch.question'),
      text: t('paywall.faq.switch.answer'),
    },
    {
      title: t('paywall.faq.refund.question'),
      text: t('web.paywall.faq6.a'),
    },
  ];

  return faqs.map((f, index) => (
    <FAQCard
      key={`faq-${index}`}
      title={f.title}
      text={f.text}
      isFirst={index === 0}
      isOpened={index === vm.openedFaqIndex}
      onPress={() => vm.onFAQPress(index)}
    />
  ));
};

export class UnauthenticatedPaywall extends React.Component {
  vm: UnauthenticatedPaywallViewModel;

  constructor(props: any) {
    super(props);
    this.vm = new UnauthenticatedPaywallViewModel();
  }

  render() {
    return <PaywallInner vm={this.vm} />;
  }
}

const PaywallInner = observer(({ vm }: { vm: UnauthenticatedPaywallViewModel }) => {
  const userReviews = getUserReviews();

  return (
    <Content>
      <H1 style={{ textAlign: 'center', marginBottom: 16 }}>{t('web.paywall.pickPlan')}</H1>
      <P2 style={{ textAlign: 'center', marginBottom: 48 }}>{t('web.paywall.cancelAnytime')}</P2>
      <CardContainer style={{ marginBottom: 16 }}>
        <CardLeft>
          <ThemedHevyProLogo />
          <P1 style={{ textAlign: 'center', marginTop: 20, marginBottom: 32 }}>
            {t('web.paywall.trainingNextLevel')}
          </P1>
          <PlanComparisonTable>
            <PlanComparisonItem style={{ border: 'none' }}>
              <td></td>
              <ItemPlanContainer>
                <P3>{t('paywall.comparison.title.free')}</P3>
              </ItemPlanContainer>
              <ItemPlanContainer>
                <P1 style={{ color: 'colors.proYellow', fontSize: 16 }}>
                  {t('web.paywall.global.pro')}
                </P1>
              </ItemPlanContainer>
            </PlanComparisonItem>
            <PlanComparisonItem>
              <ItemName>{t('paywall.comparison.workouts')}</ItemName>
              <ItemPlan>
                <MicroIcon type="check" tint={colors.primary} />
              </ItemPlan>
              <ItemPlan>
                <MicroIcon type="check" tint={colors.primary} />
              </ItemPlan>
            </PlanComparisonItem>
            <PlanComparisonItem>
              <ItemName>Hevy Trainer</ItemName>
              <ItemPlan>
                <MicroIcon type="cross" tint={colors.grey} />
              </ItemPlan>
              <ItemPlan>
                <MicroIcon type="check" tint={colors.primary} />
              </ItemPlan>
            </PlanComparisonItem>
            <PlanComparisonItem>
              <ItemName>{t('web.paywall.comparison.unlimitedRoutines.title')}</ItemName>
              <ItemPlan>{t('paywall.comparison.routines.limit')}</ItemPlan>
              <ItemPlan>
                <MicroIcon type="check" tint={colors.primary} />
              </ItemPlan>
            </PlanComparisonItem>
            <PlanComparisonItem>
              <ItemName>{t('paywall.comparison.exercises.title')}</ItemName>
              <ItemPlan>{t('paywall.comparison.exercises.limit')}</ItemPlan>
              <ItemPlan>
                <MicroIcon type="check" tint={colors.primary} />
              </ItemPlan>
            </PlanComparisonItem>
            <PlanComparisonItem>
              <ItemName>{t('paywall.comparison.measurement.title')}</ItemName>
              <ItemPlan>{t('web.paywall.comparison.measurement.limit')}</ItemPlan>
              <ItemPlan>
                <MicroIcon type="check" tint={colors.primary} />
              </ItemPlan>
            </PlanComparisonItem>
            <PlanComparisonItem>
              <ItemName>{t('paywall.comparison.history.title')}</ItemName>
              <ItemPlan>{t('paywall.comparison.history.limit')}</ItemPlan>
              <ItemPlan>
                <MicroIcon type="check" tint={colors.primary} />
              </ItemPlan>
            </PlanComparisonItem>
          </PlanComparisonTable>
        </CardLeft>
        <CardRight>
          {vm.products.map(p => (
            <PaywallCard
              key={p.key}
              planName={p.title}
              description={p.description}
              hint={p.hint}
              price={p.price}
              discountedPrice={p.discountedPrice}
              selected={p.isSelected}
              onClick={p.onClick}
              style={{ marginBottom: 16 }}
            />
          ))}
          <PrimaryButton
            style={{ width: '100%', height: 48, marginBottom: 16 }}
            type="submit"
            title={vm.ctaTitle}
            onClick={vm.onCtaClick}
          />
        </CardRight>
        <div />
      </CardContainer>
      <BottomContent>
        <DividerLine />
        <P4 type="secondary" style={{ marginTop: 12, marginBottom: 16 }}>
          {t('web.paywall.cancelSubAnytime')}
        </P4>
        <PaymentMethods>
          <MastercardLogo />
          <StripeLogo />
          <VisaLogo />
        </PaymentMethods>
        <H3 style={{ flex: 1, textAlign: 'center', marginTop: 96 }}>
          {t('web.paywall.athletesLoveHevy')}
        </H3>
        <Ratings />
        <UserReviews>
          {userReviews.slice(0, 3).map((r, index) => (
            <ReviewCard
              key={`review-${index}`}
              title={r.title}
              date={r.date}
              author={r.author}
              description={r.description}
            />
          ))}
        </UserReviews>
        <MobileContent style={{ marginBottom: 64, marginInlineStart: -16, marginInlineEnd: -16 }}>
          <SwipeableCards
            items={userReviews.slice(0, 4)}
            renderItem={r => (
              <ReviewCardMobileContainer>
                <ReviewCard
                  title={r.title}
                  date={r.date}
                  author={r.author}
                  description={r.description}
                />
              </ReviewCardMobileContainer>
            )}
            navBulletStyle={isSelected => ({
              width: 12,
              height: 12,
              borderRadius: 12,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: colors.primary,
              backgroundColor: isSelected ? colors.primary : colors.white,
            })}
          />
        </MobileContent>
        <P1 style={{ flex: 1, textAlign: 'center', marginBottom: 64 }}>
          {t('web.paywall.andFeaturedByMany')}
        </P1>
        <FeaturedContainer>
          <FeaturedLogoCarousel />
        </FeaturedContainer>
        <H3 style={{ flex: 1, textAlign: 'center', marginTop: 96, marginBottom: 48 }}>
          {t('web.paywall.anyQuestions')}
        </H3>
        <FAQList>{renderFAQs(vm)}</FAQList>
      </BottomContent>
    </Content>
  );
});
